import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  styled,
  Button
} from '@mui/material';
import {
  Dashboard,
  Settings,
  ExitToApp,
  Lock,
} from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { companyInfo } from '../BaseURL';
import Swal from 'sweetalert2';

import ChangePasswordDialog from '../ChangePasswordDialog';

// ── Staff calls & chat ────────────────────────────────────────────────────────
//
//  ONE widget, in the header. It was previously imported twice from the same
//  file — as FloatingCallWidget and as FloatingChatWidget — but that file only
//  ever exported the chat widget, so the app rendered the same component twice
//  at identical fixed coordinates, bottom-right, on top of the page content.
//  That is what made it feel broken, and what was covering the last rows of
//  every long table.
//
//  IncomingCallModal and ActiveCallPanel stay where they are: they are modals
//  that appear in response to a call, not things you click.
import { CallProvider } from '../context/CallContext';
import { ChatProvider } from '../context/Chatcontext';
import StaffChatWidget from '../Call/StaffChatWidget';
import IncomingCallModal from '../Call/IncomingCallModal';
import ActiveCallPanel from '../Call/ActiveCallPanel';

// ── Socket logout helper ──────────────────────────────────────────────────────
import { logoutUser } from '../services/socket';

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 0,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
  zIndex: theme.zIndex.drawer + 1,
}));

const ToolbarSpacer = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

function MainLayout() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [companyInfor, setcompanyInfor] = useState();
  const [logo, setlogo] = useState('');

  // Load user info
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      setUserInfo(JSON.parse(savedUserInfo));
    } else {
      navigate('/login');
    }
  }, []);

  const loadCompang = async () => {
    try {
      const response = await axios.get(companyInfo);
      if (response.data.success) {
        setcompanyInfor(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
      });
    }
  };

  useEffect(() => { loadCompang(); }, []);

  useEffect(() => {
    if (companyInfor) setlogo(companyInfor[0].logo);
  }, [companyInfor]);

  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    if (userInfo?.trans_code) {
      logoutUser(String(userInfo.trans_code));
    }
    localStorage.removeItem('userInfo');
    navigate('/');
  };

  const UserMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      PaperProps={{ sx: { width: 220, mt: 1.5 } }}
    >
      <MenuItem onClick={() => { handleMenuClose(); setShowChangePassword(true); }}>
        <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
        <ListItemText>Change Password</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon><ExitToApp fontSize="small" /></ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    // CallProvider and ChatProvider both need the same user object
    <CallProvider user={userInfo}>
      <ChatProvider user={userInfo}>
        <Box sx={{ display: 'flex' }}>

          {/* ── App Bar ── */}
          <StyledAppBar position="fixed">
            <Toolbar>
              <Tooltip title="Go to Dashboard" arrow>
                <Box
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    cursor: 'pointer',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'background-color .15s ease',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {logo && <img src={logo} alt="Logo" style={{ height: 38 }} />}
                </Box>
              </Tooltip>

              <Tooltip title="Dashboard" arrow>
                <Button
                  color="inherit"
                  startIcon={<Dashboard />}
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    ml: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 1.75,
                    color: 'primary.main',
                    bgcolor: 'rgba(26,155,127,0.08)',
                    '&:hover': { bgcolor: 'rgba(26,155,127,0.16)' },
                  }}
                >
                  Dashboard
                </Button>
              </Tooltip>

              <Box sx={{ flexGrow: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {userInfo?.role === 'Head Of Assembly' ||
                userInfo?.role === 'System Administrator' ? (
                  <Button
                    color="inherit"
                    startIcon={<Settings />}
                    onClick={() => navigate('/dashboard/AdminPanel')}
                    sx={{
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      textTransform: 'none',
                      px: 2,
                    }}
                  >
                    Admin Panel
                  </Button>
                ) : ''}

                {/* The notification bell was removed. Its badge was a
                    hard-coded "4" and its menu listed three fixed placeholder
                    entries — nothing was ever wired to real events, so it
                    only ever misinformed. */}

                {/* Staff messages & calls. In the header rather than floating
                    over the page: always in the same place, and never on top
                    of the content it used to cover. */}
                <StaffChatWidget />

                <Avatar
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  {userInfo?.name?.charAt(0)}
                </Avatar>
              </Box>
            </Toolbar>
          </StyledAppBar>

          {/* ── Page content ── */}
          <Main className="main_side">
            <ToolbarSpacer />
            <Outlet />
          </Main>

          {/* ── Menus ── */}
          <UserMenu />

          <ChangePasswordDialog
            open={showChangePassword}
            userInfo={userInfo}
            onClose={() => setShowChangePassword(false)}
          />

          {/* ── Call surfaces ──
              Both are driven by CallContext, not by a click: they appear when
              a call arrives or is in progress. The button that STARTS a call
              lives in the header, inside <StaffChatWidget />. */}
          <IncomingCallModal />
          <ActiveCallPanel />

        </Box>
      </ChatProvider>
    </CallProvider>
  );
}

export default MainLayout;